import React, { createContext, useState, useContext, useEffect, useRef } from 'react'
import { useAuthFromContext } from './AuthContext'
import { blankGameBoard } from './rules/BlankGameBoard'
import { db } from './FirebaseConfig'
import { update, ref, get, remove, set, onValue } from 'firebase/database'
import { ARMIES_PER_ROUND, STRUCTURES_TO_WIN, generateWarCapacities } from './rules/gameConfig'
import { isV3Homefront } from './rules/homefrontMaker'
import { assertWarGrid } from './rules/structureKills'
import { RULES_VERSION } from './rules/config'
import { BOT_UID, BOT_NAME, botHomefront, isBotUid } from './bot/botOpponent'
import { botByUid, botLabel } from './bot/roster'
import { isTestMode } from './utils/testMode'
import { bucketFor, archiveMeta, archiveInitialHomefront, archiveResult } from './utils/warArchive'

function parseGameData(rawGame) {
    return {
        ...rawGame,
        p1State: JSON.parse(rawGame.p1State),
        p2State: JSON.parse(rawGame.p2State),
        p1LastRound: JSON.parse(rawGame.p1LastRound),
        p2LastRound: JSON.parse(rawGame.p2LastRound),
    }
}

// Omits visually ambiguous characters (0/O, 1/I, 2/Z, 5/S)
const CODE_CHARS = 'ABCDEFGHJKLMNPQRTUVWXY3467899'
const ROOM_CODE_LENGTH = 5
// Rooms with no second player expire after 30 minutes
const ROOM_EXPIRY_MS = 30 * 60 * 1000

function generateRoomCode() {
    return Array.from({ length: ROOM_CODE_LENGTH }, () =>
        CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
    ).join('')
}

const GameContext = createContext()

export const useGameContext = () => useContext(GameContext)

export function GameProvider({ children }) {
    const { user } = useAuthFromContext()
    const [game, setGame] = useState(null)
    // 'idle' | 'waiting' | 'setup-homefront' | 'in-game'
    const [lobbyStatus, setLobbyStatus] = useState('idle')
    const [myRoomCode, setMyRoomCode] = useState(null)
    const [roomError, setRoomError] = useState(null)
    const [isJoining, setIsJoining] = useState(false)
    const [homefront, setHomefront] = useState(null)
    const unsubscribeRef = useRef(null)
    const gameRef_current = useRef(null)   // tracks current gameId for homefront-ready writes
    const isPlayer1Ref = useRef(null)
    // { id, archiveBucket } of the subscribed war — the war archive's address
    // (utils/warArchive). Mirrors the live doc so writers that run off a ref
    // (saveHomefront) don't depend on React state having caught up.
    const archiveInfoRef = useRef(null)

    useEffect(() => {
        return () => { if (unsubscribeRef.current) unsubscribeRef.current() }
    }, [])

    // Load the player's saved v3 homefront on sign-in. Stored as a JSON string:
    // RTDB strips null values from raw objects, and null resourceType (empty
    // land / firebreak) is meaningful in the v3 model.
    useEffect(() => {
        if (!user) { setHomefront(null); return }
        get(ref(db, `users/${user.uid}/homefrontV3`)).then(snap => {
            if (!snap.exists()) { setHomefront(null); return }
            try {
                const parsed = JSON.parse(snap.val())
                setHomefront(isV3Homefront(parsed) ? parsed : null)
            } catch {
                setHomefront(null)
            }
        })
    }, [user])

    // On auth change, attempt to recover in-progress session from sessionStorage.
    // This handles page refreshes during a waiting or active game.
    useEffect(() => {
        if (!user) return
        const stored = sessionStorage.getItem('hogRoomSession')
        if (!stored) return
        const session = JSON.parse(stored)
        if (Date.now() - session.createdAt > ROOM_EXPIRY_MS) {
            sessionStorage.removeItem('hogRoomSession')
            return
        }
        recoverSession(user.uid, session)
    }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

    async function recoverSession(uid, session) {
        const roomSnap = await get(ref(db, `rooms/${session.roomCode}`))
        if (!roomSnap.exists()) { sessionStorage.removeItem('hogRoomSession'); return }
        const room = roomSnap.val()
        if (room.status === 'completed') { sessionStorage.removeItem('hogRoomSession'); return }

        const gameSnap = await get(ref(db, `games/${session.gameId}`))
        if (!gameSnap.exists()) { sessionStorage.removeItem('hogRoomSession'); return }
        const gameData = gameSnap.val()
        if (gameData.player1 !== uid && gameData.player2 !== uid) {
            sessionStorage.removeItem('hogRoomSession')
            return
        }

        // Restore the refs that saveHomefront / subscribeToGame depend on. Without these,
        // a post-refresh homefront lock skips the ready-flag write and gets stuck on setup.
        isPlayer1Ref.current = gameData.player1 === uid
        gameRef_current.current = session.gameId

        setMyRoomCode(session.roomCode)
        if (room.status === 'waiting') {
            setLobbyStatus('waiting')
        } else {
            setLobbyStatus('in-game')
        }
        subscribeToGame(session.gameId)
    }

    function subscribeToGame(gameId) {
        if (unsubscribeRef.current) unsubscribeRef.current()
        const gameRef = ref(db, `games/${gameId}`)
        unsubscribeRef.current = onValue(gameRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = parseGameData(snapshot.val())
                archiveInfoRef.current = { id: data.id, archiveBucket: data.archiveBucket ?? null }
                setGame(data)

                const iP1 = isPlayer1Ref.current
                const myReady  = iP1 ? data.p1HomefrontReady : data.p2HomefrontReady
                const oppReady = iP1 ? data.p2HomefrontReady : data.p1HomefrontReady

                if (!data.player2) {
                    // Opponent hasn't joined yet. If we've already locked our homefront,
                    // move off the setup screen to waiting.
                    if (myReady) setLobbyStatus('waiting')
                    return
                }

                if (myReady && oppReady) {
                    setLobbyStatus('in-game')
                } else if (!myReady) {
                    setLobbyStatus('setup-homefront')
                } else {
                    setLobbyStatus('waiting')
                }
            } else {
                setGame(null)
                setLobbyStatus('idle')
                setMyRoomCode(null)
                sessionStorage.removeItem('hogRoomSession')
            }
        })
    }

    async function createRoom() {
        if (!user) return
        setRoomError(null)

        const stored = sessionStorage.getItem('hogRoomSession')
        if (stored) {
            const session = JSON.parse(stored)
            if (Date.now() - session.createdAt <= ROOM_EXPIRY_MS) {
                const roomSnap = await get(ref(db, `rooms/${session.roomCode}`))
                if (roomSnap.exists() && roomSnap.val().status === 'waiting') {
                    setMyRoomCode(session.roomCode)
                    setLobbyStatus('waiting')
                    subscribeToGame(session.gameId)
                    return
                }
            }
            await remove(ref(db, `rooms/${session.roomCode}`)).catch(() => {})
            await remove(ref(db, `games/${session.gameId}`)).catch(() => {})
            sessionStorage.removeItem('hogRoomSession')
        }

        // Generate a collision-resistant room code (up to 5 attempts)
        let code = null
        for (let i = 0; i < 5; i++) {
            const candidate = generateRoomCode()
            const existing = await get(ref(db, `rooms/${candidate}`))
            if (!existing.exists()) { code = candidate; break }
        }
        if (!code) {
            setRoomError('Could not generate a room code. Please try again.')
            return
        }

        const gameId = `${Date.now()}`
        const now = Date.now()
        const warCapacities = generateWarCapacities(Math.random)

        // The creator's test-mode declaration rides on the ROOM: the rules let
        // any signed-in player read a room, but a game doc only its two
        // players — and the joiner isn't one yet when they need to know.
        const isTest = isTestMode()
        await set(ref(db, `rooms/${code}`), { gameId, createdAt: now, status: 'waiting', isTest })
        await set(ref(db, `games/${gameId}`), {
            id: gameId,
            roomCode: code,
            createdAt: now,
            startedAt: null,
            endedAt: null,
            winnerId: null,
            player1: user.uid,
            player2: null,
            player1Score: 0,
            player2Score: 0,
            p1CurrentRound: 1,
            p2CurrentRound: 1,
            p1ArmiesToDeploy: ARMIES_PER_ROUND,
            p2ArmiesToDeploy: ARMIES_PER_ROUND,
            warCapacities,
            p1State: JSON.stringify(blankGameBoard(warCapacities)),
            p2State: JSON.stringify(blankGameBoard(warCapacities)),
            p1LastRound: JSON.stringify(blankGameBoard(warCapacities)),
            p2LastRound: JSON.stringify(blankGameBoard(warCapacities)),
            p1AttackResults: false,
            p2AttackResults: false,
            attackSubmittedThisRound: false,
            // Drop zones declared at submit (own-board column indices) — the
            // resupply missiles they summon are delivered at next resolution.
            p1DropZones: JSON.stringify([]),
            p2DropZones: JSON.stringify([]),
            // Homefront v3: each player builds (or keeps) a custom subplot
            // homefront on the maker screen; p{1,2}Homefront is written when
            // they lock in (saveHomefront) and the war starts once both are ready.
            p1HomefrontReady: false,
            p2HomefrontReady: false,
            // The scoreboard: structures each player has lost. The first to
            // STRUCTURES_TO_WIN loses the war (structureKills.js).
            p1StructuresLost: 0,
            p2StructuresLost: 0,
            // The rules this war is fought under (rules/config.js) — bumped on
            // any change to how a round resolves.
            rulesVersion: RULES_VERSION,
            // The creator's test-mode declaration (utils/testMode). The joiner
            // merges theirs in and freezes the archive bucket at join.
            isTest,
        })

        sessionStorage.setItem('hogRoomSession', JSON.stringify({ roomCode: code, gameId, createdAt: now }))
        isPlayer1Ref.current = true
        gameRef_current.current = gameId
        setMyRoomCode(code)
        setLobbyStatus('waiting')
        subscribeToGame(gameId)
    }

    // A war against the search bot: a room like any other, joined at once by
    // the bot as player 2 with an auto-generated Grid, so the player goes
    // straight to setting up theirs. The bot's turns are played from this
    // browser (bot/botOpponent.js). Bot wars are archived as test wars.
    async function createBotWar() {
        if (!user) return
        await createRoom()
        // createRoom may have reused a waiting room from this session: its ids
        // are in the session either way.
        const session = JSON.parse(sessionStorage.getItem('hogRoomSession') ?? '{}')
        const gameId = session.gameId ?? gameRef_current.current
        if (!gameId) return
        gameRef_current.current = gameId
        isPlayer1Ref.current = true
        const now = Date.now()
        const archiveBucket = bucketFor(true)
        const botHf = JSON.stringify(botHomefront())
        await update(ref(db, `games/${gameId}`), {
            player2: BOT_UID,
            startedAt: now,
            isTest: true,
            archiveBucket,
            p2HomefrontReady: true,
            p2Homefront: botHf,
        })
        if (session.roomCode) await update(ref(db, `rooms/${session.roomCode}`), { status: 'active' })
        archiveInfoRef.current = { id: gameId, archiveBucket }
        // The archive's opening record, as joinRoom writes for a human war,
        // plus the bot's Hive: it never goes through homefront setup.
        get(ref(db, `games/${gameId}`))
            .then(snap => writeArchiveMeta({ ...(snap.exists() ? snap.val() : {}), id: gameId, archiveBucket, isTest: true, player2: BOT_UID, startedAt: now }))
            .catch(e => console.warn('War archive meta skipped:', e?.message ?? e))
        archiveInitialHomefront(archiveInfoRef.current, 'p2', botHf)
        setLobbyStatus('setup-homefront')
    }

    async function joinRoom(code) {
        if (!user) return
        const normalized = code.trim().toUpperCase()
        setRoomError(null)
        setIsJoining(true)

        const stored = sessionStorage.getItem('hogRoomSession')
        if (stored) {
            const session = JSON.parse(stored)
            await remove(ref(db, `rooms/${session.roomCode}`)).catch(() => {})
            await remove(ref(db, `games/${session.gameId}`)).catch(() => {})
            sessionStorage.removeItem('hogRoomSession')
        }

        const roomRef = ref(db, `rooms/${normalized}`)
        const roomSnap = await get(roomRef)

        if (!roomSnap.exists()) {
            setRoomError('Room not found. Check the code and try again.')
            setIsJoining(false)
            return
        }

        const room = roomSnap.val()

        if (Date.now() - room.createdAt > ROOM_EXPIRY_MS) {
            await remove(roomRef)
            await remove(ref(db, `games/${room.gameId}`))
            setRoomError('That room code has expired.')
            setIsJoining(false)
            return
        }

        if (room.status !== 'waiting') {
            setRoomError('Room is full.')
            setIsJoining(false)
            return
        }

        // Firebase RTDB runTransaction always starts with null currentData on a
        // fresh/incognito session because get() does not warm the transaction cache.
        // For a low-concurrency 2-player scenario, a plain update is safe: the
        // server-fresh get() above already confirmed the room is 'waiting'.
        const gameRef = ref(db, `games/${room.gameId}`)
        const now = Date.now()
        // Either player carrying test mode marks the war a test. The archive
        // bucket is frozen HERE, with both players known, and every later
        // archive writer reads it off the live doc (utils/warArchive). The
        // creator's flag comes off the room — the game doc is unreadable to
        // us until this very update makes us player2.
        const isTest = !!room.isTest || isTestMode()
        const archiveBucket = bucketFor(isTest)
        await update(roomRef, { status: 'active' })
        await update(gameRef, {
            player2: user.uid,
            startedAt: now,
            isTest,
            archiveBucket,
        })
        archiveInfoRef.current = { id: room.gameId, archiveBucket }
        get(gameRef)
            .then(snap => writeArchiveMeta({ ...(snap.exists() ? snap.val() : {}), id: room.gameId, archiveBucket, isTest, player2: user.uid, startedAt: now }))
            .catch(e => console.warn('War archive meta skipped:', e?.message ?? e))

        sessionStorage.setItem('hogRoomSession', JSON.stringify({ roomCode: normalized, gameId: room.gameId, createdAt: room.createdAt }))
        isPlayer1Ref.current = false
        gameRef_current.current = room.gameId
        setMyRoomCode(normalized)
        setIsJoining(false)
        setLobbyStatus('setup-homefront')
        subscribeToGame(room.gameId)
    }

    async function renewRoom() {
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
        const stored = sessionStorage.getItem('hogRoomSession')
        if (stored) {
            const session = JSON.parse(stored)
            await remove(ref(db, `rooms/${session.roomCode}`)).catch(() => {})
            await remove(ref(db, `games/${session.gameId}`)).catch(() => {})
            sessionStorage.removeItem('hogRoomSession')
        }
        setMyRoomCode(null)
        await createRoom()
    }

    function returnToLobby() {
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
        setLobbyStatus('idle')
        setMyRoomCode(null)
    }

    async function leaveRoom() {
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
        // A war walked out on mid-fight is still a war: record the abandon
        // before the live doc goes. A completed war already holds its result
        // (archiveResult keeps the first write) and a room nobody joined was
        // never archived (no archiveBucket → no-op).
        if (game?.player2 && game?.endedAt == null) {
            await archiveResult(game, {
                status: 'abandoned',
                abandonedBy: user?.uid ?? null,
                endedAt: Date.now(),
                roundsResolved: game.attackSubmittedForRound ? game.attackSubmittedForRound - 1 : 0,
            })
        }
        if (myRoomCode) await remove(ref(db, `rooms/${myRoomCode}`)).catch(() => {})
        if (game?.id) await remove(ref(db, `games/${game.id}`)).catch(() => {})
        sessionStorage.removeItem('hogRoomSession')
        setGame(null)
        setMyRoomCode(null)
        setLobbyStatus('idle')
        setRoomError(null)
    }

    async function saveHomefront(homefrontV3) {
        if (!user) return
        // A war is decided by STRUCTURES_TO_WIN kills, so a Grid fielding fewer
        // structures can't be fought over — refuse it loudly rather than clamp.
        assertWarGrid(homefrontV3, 'Your Grid')
        // Persist the player's homefront for future wars (serialized: RTDB would
        // otherwise strip the meaningful null resourceType / structureId fields).
        const serialized = JSON.stringify(homefrontV3)
        await set(ref(db, `users/${user.uid}/homefrontV3`), serialized)
        setHomefront(homefrontV3)

        // Flip our homefront-ready flag and write the war copy of the homefront.
        // subscribeToGame handles all status transitions after this write fires.
        const gameId = gameRef_current.current
        if (gameId) {
            const flag  = isPlayer1Ref.current ? 'p1HomefrontReady' : 'p2HomefrontReady'
            const hfKey = isPlayer1Ref.current ? 'p1Homefront'      : 'p2Homefront'
            await update(ref(db, `games/${gameId}`), {
                [flag]:  true,
                [hfKey]: serialized,
            })
            // The Grid this player opened the war with — the live copy is
            // overwritten post-crater every round.
            await archiveInitialHomefront(archiveInfoRef.current, isPlayer1Ref.current ? 'p1' : 'p2', serialized)
        }
    }

    // The archive's opening record: who fought, where, on which build. `live`
    // is the raw game doc (plus the join-time fields) — not React state,
    // which hasn't seen the join yet.
    async function writeArchiveMeta(live) {
        const readUser = async (uid) => {
            if (!uid) return null
            if (isBotUid(uid)) return { uid, displayName: botLabel(botByUid(uid)), isAnonymous: null }
            const snap = await get(ref(db, `users/${uid}`)).catch(() => null)
            const u = snap?.exists() ? snap.val() : {}
            return { uid, displayName: u.displayName ?? 'Unknown', isAnonymous: u.isAnonymous ?? null }
        }
        const [p1, p2] = await Promise.all([readUser(live.player1), readUser(live.player2)])
        await archiveMeta(live, {
            id: live.id,
            roomCode: live.roomCode ?? null,
            createdAt: live.createdAt ?? null,
            startedAt: live.startedAt ?? null,
            isTest: !!live.isTest,
            bucket: live.archiveBucket,
            hostname: typeof window !== 'undefined' ? window.location.hostname : null,
            commit: process.env.REACT_APP_COMMIT_REF ?? null,
            buildContext: process.env.REACT_APP_CONTEXT ?? null,
            config: { structuresToWin: STRUCTURES_TO_WIN, armiesPerRound: ARMIES_PER_ROUND },
            rulesVersion: live.rulesVersion ?? RULES_VERSION,
            warCapacities: live.warCapacities ?? null,
            players: { p1, p2 },
        })
    }

    async function recordMatchResult(outcome) {
        if (!game || !user) return
        const isPlayer1 = user.uid === game.player1
        const opponentId = isPlayer1 ? game.player2 : game.player1
        if (!opponentId) return

        const now = Date.now()
        const vsBot = isBotUid(opponentId)
        const [selfSnap, oppSnap] = await Promise.all([
            get(ref(db, `users/${user.uid}`)),
            vsBot ? Promise.resolve(null) : get(ref(db, `users/${opponentId}`)),
        ])
        const selfName = selfSnap.exists() ? (selfSnap.val().displayName ?? 'Unknown') : 'Unknown'
        const opponentName = vsBot ? BOT_NAME : oppSnap.exists() ? (oppSnap.val().displayName ?? 'Unknown') : 'Unknown'

        const baseRecord = {
            matchId: game.id,
            startedAt: game.startedAt ?? game.createdAt,
            endedAt: now,
        }
        const opponentOutcome = outcome === 'win' ? 'loss' : outcome === 'loss' ? 'win' : 'draw'

        try {
            await Promise.all([
                set(ref(db, `matchHistory/${user.uid}/${game.id}`), {
                    ...baseRecord, opponentId, opponentName, outcome,
                }),
                // The bot keeps no match history.
                vsBot ? Promise.resolve() : set(ref(db, `matchHistory/${opponentId}/${game.id}`), {
                    ...baseRecord, opponentId: user.uid, opponentName: selfName, outcome: opponentOutcome,
                }),
                update(ref(db, `games/${game.id}`), {
                    endedAt: now,
                    winnerId: outcome === 'win' ? user.uid : outcome === 'loss' ? opponentId : null,
                }),
                myRoomCode ? update(ref(db, `rooms/${myRoomCode}`), { status: 'completed' }) : Promise.resolve(),
            ])
        } catch (e) {
            if (!e?.message?.includes('PERMISSION_DENIED')) throw e
        }

        // The archive's closing record. Both clients get here; the first
        // write stands (archiveResult keeps what exists).
        await archiveResult(game, {
            status: 'completed',
            outcome: outcome === 'win' ? (isPlayer1 ? 'p1' : 'p2') : outcome === 'loss' ? (isPlayer1 ? 'p2' : 'p1') : 'draw',
            winnerId: outcome === 'win' ? user.uid : outcome === 'loss' ? opponentId : null,
            endedAt: now,
            // attackSubmittedForRound is the bumped counter (2 after round 1 resolves).
            roundsResolved: game.attackSubmittedForRound != null ? game.attackSubmittedForRound - 1 : null,
            recordedBy: user.uid,
        })

        sessionStorage.removeItem('hogRoomSession')
    }

    const value = {
        game,
        lobbyStatus,
        myRoomCode,
        roomError,
        setRoomError,
        isJoining,
        homefront,
        saveHomefront,
        createRoom,
        createBotWar,
        joinRoom,
        leaveRoom,
        returnToLobby,
        renewRoom,
        recordMatchResult,
    }

    return (
        <GameContext.Provider value={value}>
            {children}
        </GameContext.Provider>
    )
}
